import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  Alert, 
  Paper,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Divider,
  Chip,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Fade,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Person, 
  Email, 
  Phone, 
  AccountBalance, 
  CheckCircle, 
  Security,
  AccountBox,
  TrendingUp
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { useNavigate } from 'react-router-dom';

const RegisterPage = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [checkingAffiliation, setCheckingAffiliation] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    mobileNumber: '',
    email: '',
    xmAccountNumber: '',
    weltradeAccountNumber: '',
    tradingFinancials: false,
    tradingSynthetics: false,
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    affiliationVerified: false
  });

  const steps = ['Personal Info', 'Trading Accounts', 'Security'];

  const handleInputChange = (field) => (event) => {
    let value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    
    // For account numbers, only allow numbers
    if ((field === 'xmAccountNumber' || field === 'weltradeAccountNumber') && value) {
      value = value.replace(/[^0-9]/g, '');
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    // Clear affiliation verification if XM account number changes
    if (field === 'xmAccountNumber' && formData.affiliationVerified) {
      setFormData(prev => ({
        ...prev,
        affiliationVerified: false
      }));
      setSuccessMessage('');
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 0: // Personal Info
        if (!formData.firstName.trim()) {
          newErrors.firstName = 'First name is required';
        }
        if (!formData.lastName.trim()) {
          newErrors.lastName = 'Last name is required';
        }
        if (!formData.email.trim()) {
          newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          newErrors.email = 'Email is invalid';
        }
        if (!formData.mobileNumber.trim()) {
          newErrors.mobileNumber = 'Mobile number is required';
        } else if (!/^\+?[\d\s-()]{10,}$/.test(formData.mobileNumber)) {
          newErrors.mobileNumber = 'Please enter a valid mobile number';
        }
        break;
        
      case 1: // Trading Accounts
        if (!formData.xmAccountNumber.trim() && !formData.weltradeAccountNumber.trim()) {
          newErrors.xmAccountNumber = 'At least one account number is required';
          newErrors.weltradeAccountNumber = 'At least one account number is required';
        }
        
        // Validate account number formats (numbers only)
        if (formData.xmAccountNumber.trim() && !/^\d+$/.test(formData.xmAccountNumber)) {
          newErrors.xmAccountNumber = 'XM account number must contain only numbers';
        }
        if (formData.weltradeAccountNumber.trim() && !/^\d+$/.test(formData.weltradeAccountNumber)) {
          newErrors.weltradeAccountNumber = 'Weltrade user id must contain only numbers';
        }
        
        // Check XM affiliation requirement
        if (formData.xmAccountNumber.trim() && !formData.affiliationVerified) {
          newErrors.xmAccountNumber = 'Please verify XM account affiliation by clicking "Check Affiliation"';
        }
        
        if (!formData.tradingFinancials && !formData.tradingSynthetics) {
          newErrors.tradingType = 'Please select at least one trading type';
        }
        break;
        
      case 2: // Security
        if (!formData.password) {
          newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
          newErrors.password = 'Password must be at least 8 characters';
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
          newErrors.password = 'Password must contain uppercase, lowercase, and number';
        }
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
        if (!formData.agreeToTerms) {
          newErrors.agreeToTerms = 'You must agree to the terms and conditions';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const checkXMAffiliation = async () => {
    if (!formData.xmAccountNumber.trim()) {
      setErrors(prev => ({
        ...prev,
        xmAccountNumber: 'Please enter XM account number first'
      }));
      return;
    }
    
    setCheckingAffiliation(true);
    setErrors(prev => ({ ...prev, xmAccountNumber: '' }));
    setSuccessMessage(''); // Clear any previous success messages
    
    try {
      // Call the Firebase function for affiliation check
      const response = await fetch(
        `https://us-central1-fxwars.cloudfunctions.net/checkAffiliation?accountNumber=${encodeURIComponent(formData.xmAccountNumber)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.isAffiliated) {
          setSuccessMessage('XM account affiliation verified successfully!');
          // Update form data to mark affiliation as checked
          setFormData(prev => ({ ...prev, affiliationVerified: true }));
        } else {
          setErrors(prev => ({
            ...prev,
            xmAccountNumber: 'This XM account is not affiliated with our program. Please check your account number or contact support.'
          }));
          setFormData(prev => ({ ...prev, affiliationVerified: false }));
        }
      } else {
        throw new Error(data.message || 'Failed to verify affiliation');
      }
      
    } catch (error) {
      console.error('Affiliation check error:', error);
      setErrors(prev => ({
        ...prev,
        xmAccountNumber: 'Unable to verify affiliation at this time. Please try again later.'
      }));
      setFormData(prev => ({ ...prev, affiliationVerified: false }));
    } finally {
      setCheckingAffiliation(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;
    
    setLoading(true);
    setErrors({});
    
    try {
      // Create user account with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      
      const user = userCredential.user;
      
      // Save user data to Firestore
      await setDoc(doc(db, 'users', user.uid), {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        mobileNumber: formData.mobileNumber,
        xmAccountNumber: formData.xmAccountNumber || null,
        weltradeAccountNumber: formData.weltradeAccountNumber || null,
        tradingFinancials: formData.tradingFinancials,
        tradingSynthetics: formData.tradingSynthetics,
        createdAt: new Date(),
        isVerified: false,
        affiliationChecked: formData.affiliationVerified
      });
      
      setSuccessMessage('Registration successful! Welcome to FX Wars!');
      
      // Redirect to home page after success
      setTimeout(() => {
        navigate('/');
      }, 2000);
      
    } catch (error) {
      console.error('Registration error:', error);
      
      if (error.code === 'auth/email-already-in-use') {
        setErrors({ email: 'Email address is already registered' });
      } else if (error.code === 'auth/weak-password') {
        setErrors({ password: 'Password is too weak' });
      } else {
        setErrors({ general: 'Registration failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const renderPersonalInfo = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="First Name"
          variant="outlined"
          value={formData.firstName}
          onChange={handleInputChange('firstName')}
          error={!!errors.firstName}
          helperText={errors.firstName}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person color="primary" />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Last Name"
          variant="outlined"
          value={formData.lastName}
          onChange={handleInputChange('lastName')}
          error={!!errors.lastName}
          helperText={errors.lastName}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person color="primary" />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          variant="outlined"
          value={formData.email}
          onChange={handleInputChange('email')}
          error={!!errors.email}
          helperText={errors.email}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email color="primary" />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Mobile Number"
          variant="outlined"
          value={formData.mobileNumber}
          onChange={handleInputChange('mobileNumber')}
          error={!!errors.mobileNumber}
          helperText={errors.mobileNumber || 'Include country code (e.g., +1234567890)'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Phone color="primary" />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
    </Grid>
  );

  const renderTradingAccounts = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom color="primary">
          Trading Account Information
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Please provide at least one trading account number. This helps us verify your trading experience.
        </Typography>
      </Grid>
      
      <Grid item xs={12} sm={8}>
        <TextField
          fullWidth
          label="XM Account Number"
          variant="outlined"
          value={formData.xmAccountNumber}
          onChange={handleInputChange('xmAccountNumber')}
          error={!!errors.xmAccountNumber}
          helperText={errors.xmAccountNumber || 'Enter numbers only'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AccountBalance color="primary" />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Button
          fullWidth
          variant="outlined"
          onClick={checkXMAffiliation}
          disabled={checkingAffiliation || !formData.xmAccountNumber.trim()}
          sx={{ height: '56px' }}
        >
          {checkingAffiliation ? (
            <CircularProgress size={24} />
          ) : (
            'Check Affiliation'
          )}
        </Button>
      </Grid>
      
      {/* Success message for XM affiliation verification */}
      {successMessage && formData.affiliationVerified && (
        <Grid item xs={12}>
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
            {successMessage}
          </Alert>
        </Grid>
      )}
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Weltrade User ID"
          variant="outlined"
          value={formData.weltradeAccountNumber}
          onChange={handleInputChange('weltradeAccountNumber')}
          error={!!errors.weltradeAccountNumber}
          helperText={errors.weltradeAccountNumber || 'Enter numbers only (optional)'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <TrendingUp color="primary" />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom color="primary">
          Trading Preferences
        </Typography>
        {errors.tradingType && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.tradingType}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.tradingFinancials}
                  onChange={handleInputChange('tradingFinancials')}
                  color="primary"
                />
              }
              label="Trading Financials"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.tradingSynthetics}
                  onChange={handleInputChange('tradingSynthetics')}
                  color="primary"
                />
              }
              label="Trading Synthetics"
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  const renderSecurity = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom color="primary">
          Account Security
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Create a secure password for your FX Wars account.
        </Typography>
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Password"
          type={showPassword ? 'text' : 'password'}
          variant="outlined"
          value={formData.password}
          onChange={handleInputChange('password')}
          error={!!errors.password}
          helperText={errors.password || 'Minimum 8 characters with uppercase, lowercase, and number'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Security color="primary" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Confirm Password"
          type={showConfirmPassword ? 'text' : 'password'}
          variant="outlined"
          value={formData.confirmPassword}
          onChange={handleInputChange('confirmPassword')}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Security color="primary" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.agreeToTerms}
              onChange={handleInputChange('agreeToTerms')}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              I agree to the{' '}
              <Button 
                variant="text" 
                size="small" 
                sx={{ p: 0, textDecoration: 'underline' }}
                onClick={() => navigate('/terms-and-conditions')}
              >
                Terms & Conditions
              </Button>{' '}
              and{' '}
              <Button 
                variant="text" 
                size="small" 
                sx={{ p: 0, textDecoration: 'underline' }}
                onClick={() => navigate('/privacy-policy')}
              >
                Privacy Policy
              </Button>
            </Typography>
          }
        />
        {errors.agreeToTerms && (
          <Typography variant="caption" color="error" display="block">
            {errors.agreeToTerms}
          </Typography>
        )}
      </Grid>
    </Grid>
  );

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderPersonalInfo();
      case 1:
        return renderTradingAccounts();
      case 2:
        return renderSecurity();
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ 
      pt: 12, 
      pb: 8, 
      minHeight: '100vh', 
      backgroundImage: 'url(/BearsBulls.jpeg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      position: 'relative'
    }}>
      {/* Gradient Overlay for readability */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(245,247,250,0.7) 0%, rgba(195,207,226,0.7) 100%)',
          zIndex: 1,
        }}
      />
      
      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        <Container maxWidth="md">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box textAlign="center" mb={4}>
              <Typography variant="h2" gutterBottom sx={{ 
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 'bold'
              }}>
                Join FX Wars
              </Typography>
              <Typography variant="h5" color="text.secondary" mb={2}>
                Register for the Ultimate Trading Competition
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Complete your registration to start competing for massive prizes!
              </Typography>
            </Box>
          </motion.div>

          {/* Success/Error Messages */}
          {successMessage && !formData.affiliationVerified && (
            <Fade in={!!successMessage}>
              <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircle />}>
                {successMessage}
              </Alert>
            </Fade>
          )}
          
          {errors.general && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.general}
            </Alert>
          )}

          {/* Registration Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden' }}>
              {/* Stepper */}
              <Box sx={{ p: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <Stepper 
                  activeStep={activeStep} 
                  orientation={isMobile ? 'vertical' : 'horizontal'}
                  sx={{ 
                    '& .MuiStepLabel-label': { color: 'white', fontSize: { xs: '0.875rem', sm: '1rem' } },
                    '& .MuiStepIcon-root': { color: 'rgba(255,255,255,0.5)' },
                    '& .MuiStepIcon-root.Mui-active': { color: 'white' },
                    '& .MuiStepIcon-root.Mui-completed': { color: '#4CAF50' },
                    '& .MuiStepConnector-line': { borderColor: 'rgba(255,255,255,0.3)' }
                  }}
                >
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
              
              {/* Form Content */}
              <CardContent sx={{ p: 4 }}>
                {getStepContent(activeStep)}
                
                {/* Navigation Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: { xs: 2, sm: 0 },
                  mt: 4 
                }}>
                  <Button
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    variant="outlined"
                    sx={{ order: { xs: 2, sm: 1 } }}
                  >
                    Back
                  </Button>
                  
                  <Box sx={{ order: { xs: 1, sm: 2 } }}>
                    {activeStep === steps.length - 1 ? (
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading}
                        size="large"
                        sx={{
                          background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                          px: 4
                        }}
                      >
                        {loading ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                            Creating Account...
                          </>
                        ) : (
                          'Complete Registration'
                        )}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        size="large"
                        sx={{
                          background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                          px: 4
                        }}
                      >
                        Next
                      </Button>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Paper>
          </motion.div>

          {/* Additional Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Box textAlign="center" mt={4}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Button variant="text" onClick={() => navigate('/login')}>
                  Sign In Here
                </Button>
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default RegisterPage; 